import type { GeminiContent } from './types';
import { LINE_SYNC_KEY } from '../config/instances';
import { getStoreKey } from '../config/backend';

/**
 * 全 agent API 呼び出しに画面の店舗を付ける（2026-10-03 宮古の点検: セッション一覧・追加ルールが
 * 店舗で分かれておらず、那覇で教えたルールが宮古のプロンプトにも入っていた）
 */
const storeParam = (): string => {
  try { return `&store=${getStoreKey()}`; } catch { return ''; }
};

const AGENT_API_URL = process.env.REACT_APP_AGENT_API_URL
  || 'https://booking.trailernchu.co.jp/sys/agent/api.php';
const AGENT_API_KEY = LINE_SYNC_KEY;

export interface AgentChatRequest {
  system_instruction: string;
  contents: GeminiContent[];
  tools: Array<{ functionDeclarations: any[] }>;
  /** agent_prompt_rules の動的注入スコープ（Track A2）。省略時は注入なし */
  rule_scope?: 'agent' | 'email';
  /**
   * 画面が持つ判定の世代（requestIntent.AGENT_CLIENT_GUARDS）。予約エージェントはこれが無い・古いと
   * サーバが断る（2026-10-31: 開きっぱなしの古い画面で新しい判定が効かなかった）
   */
  client_guards?: number;
}

export interface AgentChatResponse {
  success: boolean;
  content?: GeminiContent;
  error?: string;
  /** サーバが最初の1手を固定したツール（決済の確認依頼での get_square_payment_status） */
  forced_tool?: string | null;
  /** 古い画面からの呼び出しとして断られた */
  outdated_client?: boolean;
  usage?: { input_tokens: number; output_tokens: number };
}

export async function postAgentChat(body: AgentChatRequest): Promise<AgentChatResponse> {
  const url = `${AGENT_API_URL}?action=chat${storeParam()}&api_key=${AGENT_API_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // 上流 (Gemini) のエラーメッセージを下流の判定で活用するため十分な長さを確保
    // （200 字だと "function call" が "cal" で切れて regex 不一致になる事例があった）
    throw new Error(`agent api error: ${res.status} ${text.slice(0, 800)}`);
  }
  return res.json();
}

/** chat 以外の agent API action を叩く汎用ヘルパー（Track A: ログ・ルール系） */
export async function postAgentAction(action: string, body: Record<string, any> = {}): Promise<any> {
  const url = `${AGENT_API_URL}?action=${encodeURIComponent(action)}${storeParam()}&api_key=${AGENT_API_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`agent api error: ${res.status} ${text.slice(0, 400)}`);
  }
  return res.json();
}

/** GET 系 agent API action（一覧取得） */
export async function getAgentAction(action: string, params: Record<string, string | number> = {}): Promise<any> {
  const query = Object.entries(params)
    .filter(([, v]) => v !== '' && v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  const url = `${AGENT_API_URL}?action=${encodeURIComponent(action)}${storeParam()}&api_key=${AGENT_API_KEY}${query ? `&${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`agent api error: ${res.status} ${text.slice(0, 400)}`);
  }
  return res.json();
}
